/* eslint-disable @typescript-eslint/no-shadow */
import { Saveable } from '®shared/data';

export enum ReadingIntentionsKey {
  Undecided = 'Undecided',
  PlanToConsume = 'PlanToConsume',
  CurrentlyConsuming = 'CurrentlyConsuming',
  HaveConsumed = 'HaveConsumed',
  DecidedNotToConsume = 'DecidedNotToConsume',
}

/**
 * At the current point in time, the v3 APIs return reading-intentions using
 * camel-cased constants. We are moving to use lower-snake-case throughout
 * all our APIs, so this normalised version (and conversion to it) is included
 * so that our event data reflects the intended enumeration values.
 *
 * Eventually, we will move to use lower-snake-cased enumeration values.
 */
export enum ReadingIntentionsNormalised {
  UNDECIDED = 'undecided',
  PLAN_TO_CONSUME = 'plan_to_consume',
  CURRENTLY_CONSUMING = 'currently_consuming',
  HAVE_CONSUMED = 'have_consumed',
  DECIDED_NOT_TO_CONSUME = 'decided_not_to_consume',
}

export function normalisedReadingIntentionStatus(
  unnormalisedReadingIntention: ReadingIntentionsKey,
): ReadingIntentionsNormalised {
  switch (unnormalisedReadingIntention) {
    case ReadingIntentionsKey.Undecided: {
      return ReadingIntentionsNormalised.UNDECIDED;
    }
    case ReadingIntentionsKey.PlanToConsume: {
      return ReadingIntentionsNormalised.PLAN_TO_CONSUME;
    }
    case ReadingIntentionsKey.CurrentlyConsuming: {
      return ReadingIntentionsNormalised.CURRENTLY_CONSUMING;
    }
    case ReadingIntentionsKey.HaveConsumed: {
      return ReadingIntentionsNormalised.HAVE_CONSUMED;
    }
    case ReadingIntentionsKey.DecidedNotToConsume: {
      return ReadingIntentionsNormalised.DECIDED_NOT_TO_CONSUME;
    }
  }
}

export class ReadingIntention {
  constructor(
    public value: ReadingIntentionsKey,
    public label: string,
    public className: string,
    public icon?: string,
  ) {}
}

const Undecided = new ReadingIntention(
  ReadingIntentionsKey.Undecided,
  'Undecided',
  'undecided',
  'far fa-circle',
);
const PlanToConsume = new ReadingIntention(
  ReadingIntentionsKey.PlanToConsume,
  'Will read',
  'will-read',
  'fas fa-star',
);
const CurrentlyConsuming = new ReadingIntention(
  ReadingIntentionsKey.CurrentlyConsuming,
  'Reading now',
  'reading',
  'fas fa-circle',
);
const HaveConsumed = new ReadingIntention(
  ReadingIntentionsKey.HaveConsumed,
  'Have read',
  'have-read',
  'fas fa-check-circle',
);
const DecidedNotToConsume = new ReadingIntention(
  ReadingIntentionsKey.DecidedNotToConsume,
  "Won't read",
  'wont-read',
  'fas fa-times-circle',
);

export const ALL_READING_INTENTIONS: ReadingIntention[] = [
  Undecided,
  PlanToConsume,
  CurrentlyConsuming,
  HaveConsumed,
  DecidedNotToConsume,
];

export const READING_INTENTIONS = {
  Undecided,
  PlanToConsume,
  CurrentlyConsuming,
  HaveConsumed,
  DecidedNotToConsume,
};

export const READING_INTENTION_KEYS: ReadingIntentionsKey[] =
  ALL_READING_INTENTIONS.map(intention => intention.value);

export interface ResourceStatus {
  resource: string;
  status: ReadingIntentionsKey;
}

export type SaveableReadingIntention = {
  status: ReadingIntentionsKey;
} & Saveable;
